import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreatePortfolioActions, useCreatePortfolioSelectors } from 'datasource/CreatePortfolio';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { AdvisoryIncomeLiquidityMetadataResponse, AdvisoryInstituteType, LoanResponse } from 'core/types/api/Api';
import { notification } from 'ui-library';
import { useFieldArray, useForm } from 'react-hook-form';
import { STATUS_OK } from 'constants/constants';
import { TPageError } from 'datasource/PortfolioProcessLayout/PortfolioFlowLayoutManager.types';

export interface ExtendedLoanResponse extends Omit<LoanResponse, 'amount'> {
    instituteListItemId?: AdvisoryInstituteType;
    /** @format double */
    amount?: number | string;
  }

type FormPropsType = {
    grossAnnualIncome: string | null;
    annualSavedAmount: string | null;
    totalLiquidity: string | null;
    plannedExpenses: string | null;
    intendedUse: string | null;
    expectedAssetInflow: string | null;
    originOfAssetInflow: string | null;
    loans: Array<ExtendedLoanResponse> | null;
}

export const useIncomeAndLiquidity = () => {
    const { t } = useTranslation();
    const { advisoryIdSelector, advisoryDataSelector, advisoryOptions } = useCreatePortfolioSelectors();
    const { saveAdvisoryDataAction } = useCreatePortfolioActions();
    const [continueButtonLoading, setContinueButtonLoading] = useState(false);
    const [pageErrors, setPageErrors] = useState<TPageError | undefined>(undefined);

    const instituteOptions = advisoryOptions?.institute || {};

    const incomeAndLiquidityData = useMemo(() => advisoryDataSelector?.metadata?.incomeLiquidity || {}, [advisoryDataSelector?.metadata?.incomeLiquidity]);

    const {
        control, handleSubmit, formState: { errors, isDirty },
        //  getValues,
    } = useForm<FormPropsType>({
        defaultValues: {
            grossAnnualIncome: incomeAndLiquidityData?.grossAnnualIncome?.toFixed(2),
            annualSavedAmount: incomeAndLiquidityData?.annualSavedAmount?.toFixed(2),
            totalLiquidity: incomeAndLiquidityData?.totalLiquidity?.toFixed(2),
            plannedExpenses: incomeAndLiquidityData?.plannedExpenses?.toFixed(2),
            intendedUse: incomeAndLiquidityData?.intendedUse,
            expectedAssetInflow: incomeAndLiquidityData?.expectedAssetInflow?.toFixed(2),
            originOfAssetInflow: incomeAndLiquidityData?.originOfAssetInflow,
            loans: incomeAndLiquidityData?.loans?.map(loan => (
                {
                    instituteListItemId: loan?.instituteListItemId,
                    amount: loan?.amount?.toFixed(2),
                }
            )),
        },
    });

    const {
        fields, append, remove,
    } = useFieldArray({
        name: 'loans',
        control,
    });

    const addNewLoan = useCallback(() => {
        append({ instituteListItemId: undefined, amount: undefined });
    }, [append]);

    const removeLoan = useCallback((index: number) => {
        remove(index);
    }, [remove]);

    const saveData = async (data: AdvisoryIncomeLiquidityMetadataResponse) => {
        const payload = { ...data };

        if (advisoryIdSelector) {
            const result:any = await saveAdvisoryDataAction({
                key: 'income-liquidity',
                payload,
                setPageErrors,
            });

            if (result?.status === STATUS_OK) {
                stepComplete();
                unlockNavigationAction();
            }
            setContinueButtonLoading(false);
        } else {
            notification.open({ content: `${t('contactGroups.somethingWentWrong')}`, type: 'error' });
            setContinueButtonLoading(false);
        }
    };

    const onSubmit = useCallback(async (dta) => {
        if (isDirty) {
            setContinueButtonLoading(true);

            await saveData({
                loans: dta?.loans?.filter((l) => l.instituteListItemId && l.amount)?.map((loan) => ({
                    amount: +loan?.amount,
                    instituteListItemId: +loan?.instituteListItemId,
                })),
                originOfAssetInflow: dta?.originOfAssetInflow ?? null,
                expectedAssetInflow: (dta?.expectedAssetInflow != null && dta?.expectedAssetInflow !== '') ? +dta?.expectedAssetInflow : null,
                intendedUse: dta?.intendedUse,
                plannedExpenses: (dta?.plannedExpenses != null && dta?.plannedExpenses !== '') ? +dta?.plannedExpenses : null,
                totalLiquidity: (dta?.totalLiquidity != null && dta?.totalLiquidity !== '') ? +dta?.totalLiquidity : null,
                annualSavedAmount: (dta?.annualSavedAmount != null && dta?.annualSavedAmount !== '') ? +dta?.annualSavedAmount : null,
                grossAnnualIncome: (dta?.grossAnnualIncome != null && dta?.grossAnnualIncome !== '') ? +dta?.grossAnnualIncome : null,
            });
        } else {
            unlockNavigationAction();
            stepComplete();
        }
    }, [isDirty]);

    const { stepComplete, unlockNavigationAction } = usePortfolioProcessNavigation({
        saveOnContinue: useCallback(handleSubmit(onSubmit), [onSubmit]),
        backDisabled: true,
        continueInProgress: continueButtonLoading,
        enableAutoStepComplete: !isDirty,
        pageErrors,
    });

    return {
        instituteOptions,
        fields,
        control,
        errors,
        addNewLoan,
        removeLoan,
    };
};
